import { VStack, Spinner, Box, HStack, Text, Input, Pressable, Badge, useMediaQuery } from "native-base";
import { useEffect, useState } from "react";
import CryptoJS from "crypto-js";
import CustomModal from "../../../../components/modal";
import CustomButton from "../../../../components/button";
import NoDataFound from "../../../../components/noDataFound";
import { getUserListingAPI, updateUserDetailsAPI } from "../../../../api/apis/workflow";
import { Toaster } from "../../../../components/toaster";
import { LoaderAnimation } from "../../../../components/spinner";
import { COLORS } from "../../../../utils/colors";
import { COMMONFONTSIZE } from "../../../../utils/fonts";
import { MOBILE_VIEW_WIDTH } from "../../../../utils/screens";
import config from "../../../../utils/config";
import { filterSVG, editIconDetailsPage } from "../../../../utils/svg";

import Svg from 'react-native-svg';

const UsersListing = () => {
    const bytes = CryptoJS.AES.decrypt(
        sessionStorage.getItem("content"),
        config.userKey
      );
      const jsonString = bytes.toString(CryptoJS.enc.Utf8);
      const userInfo = JSON.parse(jsonString);
  const toaster = Toaster();
  const [MobileWindow] = useMediaQuery({ maxWidth: MOBILE_VIEW_WIDTH });

  // Users listing states
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUpdatingUser, setIsUpdatingUser] = useState(false);
  const [editSelectedRole, setEditSelectedRole] = useState('');

  const roleOptions = [
    { value: '', label: 'All Roles' },
    { value: 'admin', label: 'Admin' },
    { value: 'initiator', label: 'Initiator' },
    { value: 'External_employee', label: 'External Employee' },
    { value: 'employee', label: 'Employee' },
  ];

  useEffect(() => {
    setIsLoading(true);
    getUsersList(1, false);
  }, []);

  useEffect(() => {
    if (users.length > 0) {
      setIsLoading(false);
    }
  }, [users]);

  // Users listing functions
  const getUsersListWithParams = async (page = 1, append = false, search = null, role = null) => {
    setIsLoadingUsers(true);
    try {
      // Use provided parameters or fall back to state values
      const searchParam = search !== null ? search : searchQuery.trim();
      const roleParam = role !== null ? role : selectedRole;
      
      const response = await getUserListingAPI(page, 10, searchParam, roleParam);
      
      if (response.error) {
        toaster(response.message || 'Failed to fetch users', 'error', 'error');
        return;
      }

      // Handle the actual response structure based on the API response
      const userData = response.data?.data?.data || [];
      const paginationData = response.data?.data?.pagination || null;

      if (append) {
        setUsers(prev => [...prev, ...userData]);
      } else {
        setUsers(userData);
      }

      // Update pagination state
      setPagination(paginationData);
      if (paginationData) {
        const hasMoreData = paginationData.page < paginationData.pages && userData.length > 0;
        setHasMore(hasMoreData);
      } else {
        setHasMore(userData.length > 0);
      }

    } catch (error) {
      console.error('Error fetching users:', error);
      toaster('Error fetching users', 'error', 'error');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const getUsersList = async (page = 1, append = false) => {
    await getUsersListWithParams(page, append, null, null);
  };

  // Edit user functionality
  const handleEditUser = (user) => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setSelectedUser(user);
    setEditSelectedRole(user.role || '');
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser || !editSelectedRole) return;
    
    LoaderAnimation.show();
    setIsUpdatingUser(true);
    try {
      const response = await updateUserDetailsAPI(selectedUser?._id, { role: editSelectedRole });
      
      if (response.error) {
        toaster(response.message || 'Failed to update user', 'error', 'error');
        return;
      }
      
      toaster(response?.data?.data?.message || 'User updated successfully', 'success', 'success');
      setShowEditModal(false);
      setSelectedUser(null);
      setEditSelectedRole('');
      // Reset pagination state and refresh the user list with current search and filter parameters
      setCurrentPage(1);
      getUsersListWithParams(1, false, searchQuery.trim(), selectedRole);
    } catch (error) {
      console.error('Error updating user:', error);
      toaster('Error updating user', 'error', 'error');
    } finally {
      setIsUpdatingUser(false);
      LoaderAnimation.hide();
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedUser(null);
    setEditSelectedRole('');
  };

  const handleSearch = () => {
    setCurrentPage(1);
    getUsersListWithParams(1, false, searchQuery.trim(), selectedRole);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
    // Keep the current filter, only clear search
    getUsersListWithParams(1, false, '', selectedRole);
  };

  const handleRoleFilter = (role) => {
    setSelectedRole(role);
    setCurrentPage(1);
    // Keep the current search, only update filter
    getUsersListWithParams(1, false, searchQuery.trim(), role);
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    getUsersListWithParams(nextPage, true, searchQuery.trim(), selectedRole);
  };

  const openFilterModal = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setShowFilterModal(true);
  };

  const closeFilterModal = () => {
    setShowFilterModal(false);
  };

  const clearFilters = () => {
    setSelectedRole('');
    setCurrentPage(1);
    // Keep the current search, only clear filter
    getUsersListWithParams(1, false, searchQuery.trim(), '');
    closeFilterModal();
  };

  // Clear icon SVG
  const clearIconSVG = (
    <path
      d='M18 6L6 18M6 6L18 18'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  );

  // Function to format role text
  const formatRoleText = (role) => {
    if (role === 'External_employee') {
      return 'External Employee';
    }
    // Capitalize first letter for other roles
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const getFilterModalChildren = () => {
    return (
      <VStack space={4} p={2}>
        <VStack space={2}>
          <Text fontWeight='bold' fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE} color={COLORS.BLACK_COLOR}>
            Role Filter
          </Text>
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            style={{
              padding: '8px 12px',
              paddingRight: '30px',
              border: `1px solid ${COLORS.BORDER_COLOR}`,
              borderRadius: '4px',
              fontSize: '14px',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 8px center',
              backgroundSize: '16px'
            }}
          >
            {roleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </VStack>
      </VStack>
    );
  };

  const getFilterModalFooter = () => {
    return (
      <HStack mb={3} justifyContent={'space-between'} width={'100%'}>
        <CustomButton
          text='Clear All'
          isOutline
          bgColor={COLORS.BLACK_COLOR}
          textColor={COLORS.BLACK_COLOR}
          onPress={clearFilters}
        />
        <CustomButton
          text='Apply Filters'
          onPress={() => {
            handleRoleFilter(selectedRole);
            closeFilterModal();
          }}
        />
      </HStack>
    );
  };

  return (
    <>
      {isLoading ? (
        <Box flex={1} alignItems="center" justifyContent="center">
          <Spinner color={COLORS.THEME_COLOR} size="lg" />
        </Box>
      ) : (
        <Box>
          {/* Users Listing Section */}
          {(users?.length > 0 || searchQuery.trim() || selectedRole) && (
            <HStack justifyContent={'end'} alignItems={'center'} mb={4}>
              <HStack space={4} alignItems={'center'} flexWrap={MobileWindow ? 'wrap' : 'nowrap'}>
                {/* Search Input with Clear Icon */}
                <Box position='relative'>
                  <Input
                    placeholder='Search users...'
                    value={searchQuery}
                    onChangeText={(text) => {
                      // Only remove leading spaces, preserve trailing and middle spaces
                      const trimmedText = text?.trimStart();
                      setSearchQuery(trimmedText);
                      // If user clears the input with backspace, trigger search immediately
                      if (!trimmedText) {
                        setCurrentPage(1);
                        getUsersListWithParams(1, false, '', selectedRole);
                      }
                    }}
                    onKeyPress={handleKeyPress}
                    isLoading={isLoadingUsers}
                    width={MobileWindow ? '200px' : '300px'}
                    border={0}
                    bg={COLORS.WHITE_COLOR}
                    _focus={{ borderColor: 'transparent' }}
                    fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                    paddingRight={searchQuery ? '40px' : '12px'}
                  />
                  {searchQuery && (
                    <Pressable
                      position='absolute'
                      right={2}
                      top={0}
                      bottom={0}
                      justifyContent='center'
                      alignItems='center'
                      onPress={handleClearSearch}
                      p={1}
                      borderRadius={4}
                      _pressed={{ bg: COLORS.BORDER_COLOR }}
                    >
                      <Svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                        {clearIconSVG}
                      </Svg>
                    </Pressable>
                  )}
                </Box>

                {/* Filter Button with Badge */}
                <Box position='relative'>
                  <Pressable
                    onPress={openFilterModal}
                    p={2}
                    borderRadius={8}
                    bg={COLORS.WHITE_COLOR}
                    _pressed={{ bg: COLORS.BORDER_COLOR }}
                  >
                    <HStack space={2} alignItems='center'>
                      <Svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                        {filterSVG}
                      </Svg>
                      <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}>Filter</Text>
                      {selectedRole && (
                        <Badge colorScheme="orange" rounded="full" variant="solid">
                          1
                        </Badge>
                      )}
                    </HStack>
                  </Pressable>
                </Box>
              </HStack>
            </HStack>
          )}

          {/* Users Table */}
          {users?.length > 0 ? (
            <Box bg={COLORS.WHITE_COLOR} borderRadius={8} borderWidth={1} borderColor={COLORS.BORDER_COLOR} overflow="hidden">
              <Box overflowX="auto">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: COLORS.BADGE_GRAY_COLOR }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Name</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>First Name</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Last Name</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Email</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>Role</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Company</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Status</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => (
                      <tr key={user._id || index} style={{ borderBottom: `1px solid ${COLORS.BORDER_COLOR}` }}>
                        <td style={{ padding: '12px' }}>
                          {user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                        </td>
                        <td style={{ padding: '12px' }}>{user.first_name || 'N/A'}</td>
                        <td style={{ padding: '12px' }}>{user.last_name || 'N/A'}</td>
                        <td style={{ padding: '12px' }}>{user.email || 'N/A'}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <Text color={COLORS.THEME_COLOR} fontWeight={'bold'}>{formatRoleText(user.role || 'N/A')}</Text>
                        </td>
                        <td style={{ padding: '12px' }}>{user?.company || 'N/A'}</td>
                        <td style={{ padding: '12px' }}>
                          <Text fontWeight={'bold'} color={user?.status === 'active' ? COLORS.SUCCESS_TOAST_COLOR : COLORS.ERROR_MESSAGE_COLOR}>{(user.status || 'N/A').charAt(0).toUpperCase() + (user.status || 'N/A').slice(1)}</Text>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {userInfo?.role === 'admin' && (
                            <Pressable
                              onPress={() => handleEditUser(user)}
                              p={1}
                              borderRadius={4}
                              opacity={userInfo?.email === user?.email ? 0.5 : 1}
                              disabled={userInfo?.email === user?.email}
                            >
                              <Svg width="38" height="38" viewBox="0 0 46 46" fill="none">
                                {editIconDetailsPage}
                              </Svg>
                            </Pressable>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>

              {isLoadingUsers && (
                <Box p={8} textAlign="center">
                  <Spinner color={COLORS.THEME_COLOR} size="lg" />
                </Box>
              )}
            </Box>
          ) : !isLoadingUsers ? (
            <NoDataFound dataName="users" />
          ) : null}

            {/* Pagination Info */}
            {pagination && users.length > 0 && (
              <Box mt={4} textAlign="center">
                <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.PARTNERS_HEADING_COLOR}>
                  Page {pagination.page} of {pagination.pages} • Total {pagination.total} users
                </Text>
              </Box>
            )}

            {/* Load More Button */}
            {hasMore && users.length > 0 && !isLoadingUsers && (
              <Box mt={4} display="flex" justifyContent="center" alignSelf={'center'}>
                <CustomButton
                  text='Load More'
                  onPress={handleLoadMore}
                  isLoading={isLoadingUsers}
                />
              </Box>
            )}
          </Box>
      )}

      {/* Filter Modal */}
      {!MobileWindow ? (
        <CustomModal
          isOpen={showFilterModal}
          onClose={closeFilterModal}
          header="Filter Users"
          body={getFilterModalChildren()}
          footer={getFilterModalFooter()}
          width="400px"
        />
      ) : (
        <CustomModal
          isOpen={showFilterModal}
          onClose={closeFilterModal}
          header="Filter Users"
          body={getFilterModalChildren()}
          footer={getFilterModalFooter()}
          maxH="60vh"
        />
      )}

      {/* Edit User Modal */}
      <CustomModal
        isOpen={showEditModal}
        onClose={closeEditModal}
        header="Edit User Role"
        body={
          <VStack space={4} p={2}>
            <Box>
              <Text color={COLORS.PARTNERS_HEADING_COLOR} fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE} mb={2}>
                User : {selectedUser?.display_name || `${selectedUser?.first_name || ''} ${selectedUser?.last_name || ''}`.trim()}
              </Text>
              <Text fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE} color={COLORS.PARTNERS_HEADING_COLOR} mb={4}>
                Email : {selectedUser?.email}
              </Text>
            </Box>
            <VStack space={2}>
              <Text fontWeight="bold" fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE} color={COLORS.BLACK_COLOR}>
                Select New Role
              </Text>
              <select
                value={editSelectedRole}
                style={{
                  padding: '8px 12px',
                  paddingRight: '30px',
                  border: `1px solid ${COLORS.BORDER_COLOR}`,
                  borderRadius: '4px',
                  fontSize: '14px',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center',
                  backgroundSize: '16px'
                }}
                onChange={(e) => setEditSelectedRole(e.target.value)}
              >
                {roleOptions.slice(1).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </VStack>
          </VStack>
        }
        footer={
          <HStack justifyContent="space-between" width="100%">
            <CustomButton
              text="Cancel"
              onPress={closeEditModal}
              isOutline
              bgColor={COLORS.BLACK_COLOR}
              textColor={COLORS.BLACK_COLOR}
            />
            <CustomButton
              text="Update"
              onPress={handleUpdateUser}
              isLoading={isUpdatingUser}
              disabled={!editSelectedRole}
            />
          </HStack>
        }
        width="400px"
      />
    </>
  );
};

export default UsersListing;
